import React, { useEffect, useState } from 'react'
import { Button, DataTable } from '@/shared/ui'
import type { Column, HoldDTO } from '@/shared/api'
import {
  useBalance,
  useHolds,
  useOperations,
  useReleaseHold,
  useUpdateBalanceSettings,
} from '@/shared/hooks'
import { useAuth } from '@/shared/contexts'
import { ConfirmActionModal } from '@/pages/balance/ConfirmActionModal'
import { InvoiceCreateModal } from '@/pages/balance/InvoiceCreateModal'
import { OperationsTable } from '@/pages/balance/OperationsTable'
import { formatMoney } from '@/pages/balance/format'

const HOLD_COLUMNS = (onRelease?: (hold: HoldDTO) => void): Column<HoldDTO>[] => [
  {
    key: 'shopOrderId',
    title: 'Заказ',
    render: (_, hold) => (hold.shopOrderId ? `Заказ #${hold.shopOrderId}` : '—'),
  },
  {
    key: 'amount',
    title: 'Сумма',
    render: (_, hold) => formatMoney(hold.amount),
  },
  {
    key: 'createdAt',
    title: 'Дата',
    render: (_, hold) => hold.createdAt || '—',
  },
  ...(onRelease
    ? [
        {
          key: 'actions' as const,
          title: 'Действия',
          render: (_: unknown, hold: HoldDTO) => (
            <Button size="sm" variant="secondary" onClick={() => onRelease(hold)}>
              Освободить
            </Button>
          ),
        },
      ]
    : []),
]

interface Props {
  entityId: number
  entityName?: string | undefined
}

/** Блок «Финансы» в карточке юрлица: баланс, резервы и последние операции. */
export const FinanceBlock: React.FC<Props> = ({ entityId, entityName }) => {
  const { hasPermission } = useAuth()
  const canView = hasPermission('balance', 'view')
  const canViewOperations = hasPermission('balance', 'view_operations')
  const canCreateInvoice = hasPermission('balance', 'create_invoice')
  const canReleaseHold = hasPermission('balance', 'release_hold')
  const canEditSettings = hasPermission('balance', 'edit_settings')

  const [createOpen, setCreateOpen] = useState(false)
  const [holdTarget, setHoldTarget] = useState<HoldDTO | null>(null)
  // Оптимистичное значение переключателя: галочка не отскакивает до рефетча.
  const [demoEnabledDraft, setDemoEnabledDraft] = useState<boolean | null>(null)

  // Резервы и операции доступны по отдельному праву view_operations.
  const operationsEnabled = canView && canViewOperations

  const balance = useBalance(entityId, { enabled: canView })
  const holds = useHolds(entityId, { enabled: operationsEnabled })
  const operations = useOperations(entityId, 1, 10, { enabled: operationsEnabled })

  const releaseHold = useReleaseHold()
  const updateSettings = useUpdateBalanceSettings()

  // Серверное значение обновилось — снимаем оптимистичную накладку.
  useEffect(() => {
    setDemoEnabledDraft(null)
  }, [balance.data?.demoEnabled])

  if (!canView) return null

  const handleRelease = async () => {
    if (!holdTarget) return
    try {
      await releaseHold.mutateAsync({ entityId, entryId: holdTarget.entryId })
      setHoldTarget(null)
    } catch {
      // Ошибку уже показал interceptor API — модалка остаётся открытой.
    }
  }

  const holdColumns = HOLD_COLUMNS(canReleaseHold ? setHoldTarget : undefined)

  const demoEnabled = demoEnabledDraft ?? balance.data?.demoEnabled ?? false

  const handleToggleDemo = (enabled: boolean) => {
    setDemoEnabledDraft(enabled)
    updateSettings.mutate(
      { entityId, enabled },
      { onError: () => setDemoEnabledDraft(null) }
    )
  }

  return (
    <div className="border-t border-gray-200 pt-4 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-base font-semibold text-gray-900">Финансы</h3>
        <div className="flex flex-wrap items-center gap-4">
          {canEditSettings ? (
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-500"
                checked={demoEnabled}
                disabled={!balance.data || updateSettings.isPending}
                onChange={(e) => handleToggleDemo(e.target.checked)}
              />
              Демо-баланс
            </label>
          ) : (
            <span className="text-sm text-gray-700">
              Демо-баланс:{' '}
              {balance.data ? (demoEnabled ? 'включён' : 'выключен') : '—'}
            </span>
          )}
          {canCreateInvoice && (
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              Создать счёт
            </Button>
          )}
        </div>
      </div>

      {balance.isLoading ? (
        <div className="text-sm text-gray-500">Загрузка...</div>
      ) : !balance.data ? (
        <div className="text-sm text-gray-500">Не удалось загрузить баланс</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="rounded-lg border border-gray-200 p-3">
            <div className="text-xs text-gray-500">Общий баланс</div>
            <div className="text-lg font-semibold text-gray-900">
              {formatMoney(balance.data.total)}
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 p-3">
            <div className="text-xs text-gray-500">Доступный</div>
            <div className="text-lg font-semibold text-gray-900">
              {formatMoney(balance.data.available)}
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 p-3">
            <div className="text-xs text-gray-500">Зарезервировано</div>
            <div className="text-lg font-semibold text-gray-900">
              {formatMoney(balance.data.reserved)}
            </div>
          </div>
        </div>
      )}

      {operationsEnabled && (
        <>
          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-gray-900">Активные резервы</h4>
            <DataTable
              data={holds.data?.items ?? []}
              columns={holdColumns}
              loading={holds.isLoading}
              emptyMessage="Активных резервов нет"
              dense
            />
          </div>

          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-gray-900">Последние операции</h4>
            <OperationsTable
              data={operations.data?.items ?? []}
              loading={operations.isLoading}
              emptyMessage="Операций пока нет"
            />
          </div>
        </>
      )}

      <InvoiceCreateModal
        isOpen={createOpen}
        entityId={entityId}
        entityName={entityName}
        onClose={() => setCreateOpen(false)}
      />

      <ConfirmActionModal
        isOpen={!!holdTarget}
        title="Освободить резерв"
        description={
          holdTarget?.shopOrderId
            ? `Резерв под заказ #${holdTarget.shopOrderId} на сумму ${formatMoney(holdTarget.amount)} будет освобождён.`
            : `Резерв на сумму ${formatMoney(holdTarget?.amount)} будет освобождён.`
        }
        confirmLabel="Освободить"
        loading={releaseHold.isPending}
        onClose={() => setHoldTarget(null)}
        onSubmit={handleRelease}
      />
    </div>
  )
}
